import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { CloseRoundButton } from 'components/Button/style.js';
import { color } from 'theme';
import {
  ModalWrapper,
  ModalContainer,
  ButtonWrapper,
  InnerContainer,
} from './style.js';

const Modal = ({
  show,
  onClose,
  children,
  showCloseBtn = true,
  disableClickOutside = false,
}) => {
  const ref = useRef();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(show);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      // Small delay to trigger CSS transition (mount first, then show)
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      // Wait for fade-out transition before unmounting
      const timer = setTimeout(() => setShouldRender(false), 500);
      return () => clearTimeout(timer);
    }
  }, [show]);

  const closeOnEscapeKeyDown = (e) => {
    if (e.keyCode === 27) {
      onClose();
    }
  };

  useEffect(() => {
    document.body.addEventListener('keydown', closeOnEscapeKeyDown);

    return function cleanup() {
      document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
    };
    // eslint-disable-next-line
  }, []);

  const handleClickOutside = (e) => {
    // Don't handle click outside if disabled
    if (disableClickOutside) return;

    // Check if click is outside this modal
    if (show && ref.current && !ref.current?.contains(e.target)) {
      // Also check if click is inside another modal (check for any modal container)
      const clickedInsideAnyModal = e.target.closest('[data-modal-container]');
      if (!clickedInsideAnyModal) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
  };

  useEffect(() => {
    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  // Don't render at all if never shown
  if (!shouldRender) return null;

  return ReactDOM.createPortal(
    <ModalWrapper $show={isVisible}>
      <ModalContainer
        $borderColor={color.lightGrey}
        $show={isVisible}
        data-modal-container="true"
        ref={ref}
      >
        <InnerContainer>
          {showCloseBtn ? (
            <ButtonWrapper>
              <CloseRoundButton
                $borderColor={color.lightGrey}
                $color={color.lightGrey}
                $hoverBg={color.lightGrey}
                onClick={onClose}
              />
            </ButtonWrapper>
          ) : null}
          {children}
        </InnerContainer>
      </ModalContainer>
    </ModalWrapper>,
    document.getElementById('root')
  );
};

export default Modal;
